import Titulo from '@/components/tipografia/Titulo';
import React, { useCallback } from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import { mdiCallMade, mdiCallReceived, mdiDownload, mdiLoading, mdiMagnify, mdiPlay, mdiTrashCan, mdiPhone, mdiWhatsapp, mdiTextBox } from '@mdi/js';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import { useInfiniteQuery, useMutation } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import { useDebounce } from '../chat/useDebounce';
import { toast } from 'react-toastify';
import Grid, { GridItem } from '@/components/layout/Grid';
import Botao from '@/components/Botao';
import { useAuth } from '@/lib/useAuth';
import { hasAuthorization, rolesAdmin, rolesUser } from '@tera/shared/src/lib/auth';
import { formatSeconds } from '@tera/shared/src/lib';
import Icon from '@/components/Icon';
import Modal from '@/components/layout/Modal';
import ModalTranscricao from './ModalTranscricao';

// Tipagem da resposta do backend
type ResponseList = {
    status: number;
    ok?: boolean;
    result: any[];
    total: number;
    cursor: string | number | null;
};

const PageLigacao = () => {
    const { user } = useAuth();

    // Player state
    const [showPlayer, setShowPlayer] = React.useState<boolean>(false);
    const [playerCallId, setPlayerCallId] = React.useState<number | null>(null);
    const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);

    // Transcrição state
    const [showTranscricao, setShowTranscricao] = React.useState<boolean>(false);
    const [transcricaoCallId, setTranscricaoCallId] = React.useState<number | null>(null);
    const [transcricaoData, setTranscricaoData] = React.useState<any>(null);

    const openPlayer = (id: number) => {
        const url = `/api/assets/chamadas/${id}/recording.ogg`;
        setPlayerCallId(id);
        setAudioUrl(url);
        setShowPlayer(true);
        // auto-play após pequena defasagem para montar o componente
        setTimeout(() => {
            try { audioRef.current?.play().catch(() => { }); } catch { }
        }, 50);
    };

    const closePlayer = () => {
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        } catch { }
        setShowPlayer(false);
        setAudioUrl(null);
        setPlayerCallId(null);
    };

    const [search, setSearch] = React.useState<string>('');
    const [estadoFiltro, setEstadoFiltro] = React.useState<string>('todos');
    const [canalFiltro, setCanalFiltro] = React.useState<string>('todos');
    const debouncedSearch = useDebounce(search, 500);

    const queryList = useInfiniteQuery({
        queryKey: ['chamada', debouncedSearch, estadoFiltro, canalFiltro],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams();
            if (debouncedSearch) params.set('txt', debouncedSearch);
            if (pageParam) params.set('c', pageParam);

            // Filtrar por estado e canal
            const filter: any = {};

            // Filtro de estado
            if (estadoFiltro !== 'todos') {
                if (estadoFiltro === 'nao_atendidas') {
                    filter['chamada.estado'] = ['in', ['expirada', 'rejeitada']];
                } else {
                    filter['chamada.estado'] = ['eq', estadoFiltro];
                }
            }

            // Filtro de canal
            if (canalFiltro !== 'todos') {
                filter['canal.tipo'] = ['eq', canalFiltro];
            }

            if (Object.keys(filter).length > 0) {
                params.set('fi', JSON.stringify(filter));
            }

            return await axios.get<ResponseList>(`/api/chamada?${params.toString()}`,
                { withCredentials: true });
        },
        getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
    });

    const deleteMutator = useMutation(
        async (id: number) => {
            return await axios.delete(`/api/chamada/${id}`, { withCredentials: true });
        },
        {
            onSuccess: () => {
                toast('Ligação apagada com sucesso', {
                    toastId: 'del-success', position: 'top-center', type: 'success', autoClose: 5000,
                });
                queryList.refetch();
            },
            onError: (err: any) => {
                console.error(err);
                toast('Ocorreu um erro ao tentar apagar a ligação.', {
                    toastId: 'del-error', position: 'top-center', type: 'warning', autoClose: 5000,
                });
            },
            onSettled: () => queryList.refetch(),
        }
    );

    const handleDelete = (id: number) => deleteMutator.mutate(id);

    const handlePlay = async (id: number) => {
        openPlayer(id);
    };

    const openTranscricao = (row: any) => {
        setTranscricaoCallId(row.id);
        setTranscricaoData(row);
        setShowTranscricao(true);
    };

    const closeTranscricao = () => {
        setShowTranscricao(false);
        setTranscricaoCallId(null);
        setTranscricaoData(null);
    };

    const headers: DataTableHeader[] = [
        {
            label: 'Código', width: '80px', column: 'id',
        },
        {
            label: 'Data', width: '20%', render: (row: any) => {
                return row.data_inicio ? new Date(row.data_inicio).toLocaleString('pt-BR') : '-';
            }
        },
        { label: 'Cliente', width: '18%', column: 'cliente_nome' },
        {
            label: 'Canal', width: '10%', render: (row: any) => {
                const canais: Record<string, { label: string; icon: string; color: string }> = {
                    voip: { label: 'VoIP', icon: mdiPhone, color: 'var(--tc-color-primary)' },
                    whatsapp: { label: 'WhatsApp', icon: mdiWhatsapp, color: '#25D366' },
                };
                const canal = canais[row.canal?.tipo];
                if (!canal) {
                    return row.canal?.nome ?? '--';
                }
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon path={canal.icon} size={0.9} color={canal.color} />
                        <span style={{ color: canal.color, fontWeight: 500 }}>{canal.label}</span>
                    </div>
                );
            }
        },
        { label: "Atendido por", width: '13%', render: (row: any) => row.usuario_responsavel?.nome ?? '--' },
        {
            label: 'Estado', width: '12%', render: (row: any) => {
                const estados: Record<string, { label: string; color: string }> = {
                    aguardando: { label: 'Aguardando', color: 'var(--tc-color-warning)' },
                    conectada: { label: 'Conectada', color: 'var(--tc-color-info)' },
                    em_atendimento: { label: 'Em atendimento', color: 'var(--tc-color-primary)' },
                    finalizada: { label: 'Finalizada', color: 'var(--tc-color-success)' },
                    expirada: { label: 'Não atendida', color: 'var(--tc-color-danger)' },
                    rejeitada: { label: 'Rejeitada', color: 'var(--tc-color-danger)' },
                    erro: { label: 'Erro', color: 'var(--tc-color-danger)' },
                };
                const estado = estados[row.estado];
                if (!estado) return row.estado ?? '--';
                return (
                    <span style={{ color: estado.color, fontWeight: 500 }}>
                        {estado.label}
                    </span>
                );
            }
        },
        {
            label: 'Duração', width: '10%', render: (row: any) => {
                // Não mostrar duração para chamadas expiradas ou rejeitadas
                if (['expirada', 'rejeitada', 'erro'].includes(row.estado)) {
                    return <span style={{ color: 'var(--tc-color-text-muted)' }}>--</span>;
                }
                if (typeof row.duracao_segundos === 'number') return formatSeconds(Math.max(0, Math.floor(row.duracao_segundos)));
                if (row.data_inicio && row.data_fim) {
                    const s = Math.max(0, Math.floor((new Date(row.data_fim).getTime() - new Date(row.data_inicio).getTime()) / 1000));
                    return formatSeconds(s);
                }
                return '-';
            }
        },
        {
            label: 'Direção', width: '10%', render: (row: any) => {
                return row.iniciador === 'cliente' ?
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon path={mdiCallReceived} size={1} color="var(--tc-color-success)" />
                        <span>Recebida</span>
                    </div>
                    :
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon path={mdiCallMade} size={1} color="var(--tc-color-danger)" />
                        <span>Efetuada</span>
                    </div>;
            }
        },
        {
            label: 'Encerrada por', width: '12%', render: (row: any) => {
                const motivos: Record<string, { label: string; color: string }> = {
                    cliente: { label: 'Cliente', color: 'var(--tc-color-info)' },
                    atendente: { label: 'Atendente', color: 'var(--tc-color-success)' },
                    sistema: { label: 'Sistema', color: 'var(--tc-color-warning)' },
                    transferencia: { label: 'Transferência', color: 'var(--tc-color-primary)' },
                };
                const motivo = motivos[row.motivo_finalizacao];
                if (!motivo) return '--';
                return (
                    <span style={{ color: motivo.color, fontWeight: 500 }}>
                        {motivo.label}
                    </span>
                );
            }
        },
        {
            label: 'Ações', width: '180px', stickRight: true, alignRight: true,
            render: (row: any) => {
                // Chamadas expiradas/rejeitadas não têm gravação
                const hasRecording = !['expirada', 'rejeitada', 'erro', 'aguardando'].includes(row.estado);
                // Verificar se tem transcrição
                const hasTranscricao = row.transcricao_roteiro?.entries?.length > 0;

                return (
                    <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
                        <Botao
                            title={hasTranscricao ? "Ver transcrição" : "Sem transcrição"}
                            variant="none-info"
                            icon={mdiTextBox}
                            onClick={() => openTranscricao(row)}
                            disabled={!hasTranscricao}
                        />
                        <Botao
                            title={hasRecording ? "Tocar" : "Sem gravação"}
                            variant="none-primary"
                            icon={mdiPlay}
                            onClick={() => handlePlay(row.id)}
                            disabled={!hasRecording}
                        />
                        {hasAuthorization(user, rolesAdmin) && (
                            <BotaoPerigoso buttonProps={{ title: 'Apagar' }} variant="none-danger" icon={mdiTrashCan} onClick={() => handleDelete(row.id)} />
                        )}
                    </HBox>
                );
            }
        },
    ];

    const queryFlatList = queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
    const total = queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;

    return (
        <LayoutUsuario>
            {/* Modal do player */}
            <Modal
                size='md'
                title={`Reproduzir ligação${playerCallId ? ` #${playerCallId}` : ''}`}
                show={showPlayer}
                onClose={closePlayer}
                hideControls
                ignoreOverlayClick
            >
                <HBox gap="8px" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <audio
                        ref={audioRef}
                        src={audioUrl ?? undefined}
                        controls
                        style={{ width: '100%' }}
                        onError={() => toast('Gravação indisponível desta ligação.', { position: 'top-center', type: 'info' })}
                    />
                    {audioUrl && (
                        <a href={audioUrl} download target="_blank" rel="noreferrer">
                            <Botao variant='outline-primary' title='Baixar' icon={mdiDownload}>Baixar</Botao>
                        </a>
                    )}
                </HBox>
            </Modal>

            {/* Modal da transcrição */}
            <ModalTranscricao
                show={showTranscricao}
                onClose={closeTranscricao}
                chamadaId={transcricaoCallId}
                transcricaoRoteiro={transcricaoData?.transcricao_roteiro}
                resumoChamada={transcricaoData?.resumo_chamada}
            />

            <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
                {/* Header */}
                <Grid>
                    <GridItem sm={12} md={6}>
                        <VBox>
                            <Titulo>Ligações</Titulo>
                            <Descricao>Consulte suas ligações e reproduza as gravações</Descricao>
                        </VBox>
                    </GridItem>
                </Grid>

                {/* Filtros */}
                <Grid>
                    <GridItem sm={12} md={6}>
                        <CampoTexto
                            label="Pesquisar"
                            prepend={<Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-500)" />}
                            placeholder="Pesquisar por cliente ou telefone"
                            value={search}
                            onChange={setSearch}
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <CampoSeletor
                            label="Canal"
                            value={canalFiltro}
                            onChange={(v) => setCanalFiltro(v ?? 'todos')}
                            options={[
                                { id: 'todos', label: 'Todos' },
                                { id: 'voip', label: 'VoIP' },
                                { id: 'whatsapp', label: 'WhatsApp' },
                            ]}
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <CampoSeletor
                            label="Estado"
                            value={estadoFiltro}
                            onChange={(v) => setEstadoFiltro(v ?? 'todos')}
                            options={[
                                { id: 'todos', label: 'Todas' },
                                { id: 'nao_atendidas', label: 'Não atendidas' },
                                { id: 'aguardando', label: 'Aguardando' },
                                { id: 'conectada', label: 'Conectada' },
                                { id: 'em_atendimento', label: 'Em atendimento' },
                                { id: 'finalizada', label: 'Finalizadas' },
                                { id: 'expirada', label: 'Expiradas' },
                                { id: 'rejeitada', label: 'Rejeitadas' },
                            ]}
                        />
                    </GridItem>
                </Grid>

                {queryList.isFetching && (
                    <div>
                        Aguarde...
                    </div>
                )}
                {!queryList.isFetching && (
                    <div>
                        Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'} resultados.
                    </div>
                )}

                <DataTable
                    isLoading={queryList.isFetching}
                    cabecalhos={headers}
                    linhas={queryFlatList ?? []}
                    hasMoreData={queryList.hasNextPage}
                    onLoadMore={async () => queryList.fetchNextPage()}
                />
            </VBox>
        </LayoutUsuario>
    );
};

export default PageLigacao;
